// Every auto-rollback breach of the job, newest first: its casebook case
// and/or breach report, each opened in a drawer.

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { ReportProblem as BreachIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { envRegionLabel } from '../lib/rollbackProfiles';
import { envRegionChoices, metaString, rollbackListKey, verdictLabel, versionChange } from '../lib/rollbackWatches';
import { getColors } from '../theme/tokens';
import { RollbackBreachItem, RollbackDocKind } from '../../types/DeploymentRecord';
import EmptyState from './EmptyState';
import Help from './Help';
import RollbackDocDrawer from './RollbackDocDrawer';
import {
  EnvRegionFilter,
  ListError,
  ListLoading,
  LoadOlder,
  mono,
  TierChip,
  UtcTime,
} from './RollbackListParts';
import { BREACH_FIELD_HELP as HELP } from '../copy/rollbackWatchHelp';

export interface RollbackBreachesTabProps {
  app: string;
  jobId: string;
}

const RollbackBreachesTab: React.FC<RollbackBreachesTabProps> = ({ app, jobId }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { wsStatus, rollbackBreaches, fetchRollbackBreaches } = useDeploymentData();
  const [envRegion, setEnvRegion] = useState('');
  const [openDoc, setOpenDoc] = useState<{ kind: RollbackDocKind; key: string } | null>(null);

  useEffect(() => {
    if (wsStatus === 'connected' && app && jobId) {
      fetchRollbackBreaches(app, jobId, { envRegion: envRegion || undefined });
    }
  }, [wsStatus, app, jobId, envRegion, fetchRollbackBreaches]);

  const slice = rollbackBreaches.get(rollbackListKey(app, jobId, envRegion));
  const all = rollbackBreaches.get(rollbackListKey(app, jobId));
  const choices = useMemo(
    () => envRegionChoices((all?.items || []).map(i => i.env_region), envRegion),
    [all?.items, envRegion]
  );
  const items = slice?.items || [];

  let body: React.ReactNode;
  if (!slice || (slice.loading && !slice.loaded)) {
    body = <ListLoading label="Loading breaches…" />;
  } else if (items.length === 0) {
    body = slice.error ? null : (
      <EmptyState
        icon={<BreachIcon />}
        title="No breaches recorded"
        body="A breach is recorded when a signal's breach condition held long enough during a watch."
      />
    );
  } else {
    body = (
      <>
        {/* Wider than the card on a narrow screen: scroll it rather than spill past the edge. */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{ '& th': { color: c.inkMuted, fontWeight: 600, backgroundColor: c.canvasSoft, whiteSpace: 'nowrap' } }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <Help title={HELP.when}>When</Help>
                </TableCell>
                <TableCell>Env/region</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>
                  <Help title={HELP.highestTierOrSignal}>Tier / signal</Help>
                </TableCell>
                <TableCell>
                  <Help title={HELP.action}>Action</Help>
                </TableCell>
                <TableCell>
                  <Help title={HELP.outcome}>Outcome</Help>
                </TableCell>
                <TableCell>
                  <Help title={HELP.rcaCategory}>RCA category</Help>
                </TableCell>
                <TableCell>
                  <Help title={HELP.verdict}>Verdict</Help>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <BreachRow
                  key={`${item.env_region}::${item.deployment_id}::${item.at}`}
                  item={item}
                  onOpen={(kind, key) => setOpenDoc({ kind, key })}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
        {slice.nextBefore && (
          <LoadOlder
            loading={slice.loading}
            onClick={() =>
              fetchRollbackBreaches(app, jobId, { envRegion: envRegion || undefined, before: slice.nextBefore })
            }
          />
        )}
      </>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: c.inkMuted, mb: 2 }}>
        Only the watches where a signal stayed past its rule. Each has a case (what happened, the decision, the
        RCA&apos;s view and, once reviewed, a verdict) and a breach report (the signal readings that set it off).
      </Typography>
      {choices.length > 0 && <EnvRegionFilter choices={choices} selected={envRegion} onSelect={setEnvRegion} />}
      <ListError error={slice?.error} />
      {body}
      <RollbackDocDrawer
        app={app}
        jobId={jobId}
        kind={openDoc?.kind}
        docKey={openDoc?.key}
        onClose={() => setOpenDoc(null)}
      />
    </Box>
  );
};

const DocButton: React.FC<{ label: string; help: string; docKey?: string; onClick: () => void }> = ({
  label,
  help,
  docKey,
  onClick,
}) => (
  <Tooltip title={docKey ? help : `No ${label.toLowerCase()} was written for this breach.`} arrow>
    {/* A disabled button fires no events, so the span carries the tooltip. */}
    <span>
      <Button
        size="small"
        variant="outlined"
        disabled={!docKey}
        onClick={onClick}
        sx={{ minWidth: 0, px: 1, whiteSpace: 'nowrap' }}
      >
        {label}
      </Button>
    </span>
  </Tooltip>
);

const BreachRow: React.FC<{
  item: RollbackBreachItem;
  onOpen: (kind: RollbackDocKind, key: string) => void;
}> = ({ item, onOpen }) => {
  const meta = item.meta;
  const fromCase = item.meta_source === 'case';
  const signal = metaString(meta, 'signal');
  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <UtcTime value={item.at} />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{envRegionLabel(item.env_region)}</TableCell>
      <TableCell sx={mono}>
        {versionChange(metaString(meta, 'previous_version'), metaString(meta, 'version'))}
      </TableCell>
      <TableCell>
        {fromCase ? (
          <TierChip tier={metaString(meta, 'highest_tier')} />
        ) : (
          <Box component="span" sx={mono}>
            {signal || '—'}
          </Box>
        )}
      </TableCell>
      <TableCell sx={mono}>{metaString(meta, 'action') || '—'}</TableCell>
      <TableCell sx={mono}>{metaString(meta, 'outcome') || '—'}</TableCell>
      <TableCell sx={mono}>{metaString(meta, 'rca_category') || '—'}</TableCell>
      <TableCell>{verdictLabel(meta)}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
          <DocButton
            label="Case"
            help={HELP.case}
            docKey={item.case_key}
            onClick={() => item.case_key && onOpen('case', item.case_key)}
          />
          <DocButton
            label="Breach report"
            help={HELP.report}
            docKey={item.report_key}
            onClick={() => item.report_key && onOpen('report', item.report_key)}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default RollbackBreachesTab;
